import { createContext, useCallback, useContext, useEffect, useRef, useState, type ReactNode } from 'react';
import { toast } from '../components/ui';
import { markActiveToday } from './hooks';

// ---------------------------------------------------------------------------
// Built-in Pomodoro timer. Lives outside the route tree (provider mounted in
// <App>) so the timer keeps running — and the floating widget keeps following
// you — no matter which page you navigate to. State is mirrored to
// localStorage so a reload resumes the same session.
// ---------------------------------------------------------------------------

export type Phase = 'focus' | 'short' | 'long';

export type PomoSettings = {
  focusMin: number;
  shortMin: number;
  longMin: number;
  longEvery: number;
  autoStartBreaks: boolean;
};

export type PomoState = {
  phase: Phase;
  running: boolean;
  /** epoch ms when the current phase ends (only meaningful while running) */
  endsAt: number;
  /** ms left, captured when paused */
  pausedMs: number;
  cycles: number;
};

export type PomoPos = { x: number; y: number };

const LS = 'hub.pomodoro.v1';

const DEFAULT_SETTINGS: PomoSettings = {
  focusMin: 25,
  shortMin: 5,
  longMin: 15,
  longEvery: 4,
  autoStartBreaks: true
};

const DEFAULT_POS: PomoPos = { x: 0, y: 0 }; // 0,0 => bottom-right default set by widget

type Persisted = {
  settings: PomoSettings;
  pos: PomoPos;
  state: PomoState;
  sessionsToday: number;
  sessionsDate: string;
};

function todayKey(): string {
  return new Date().toISOString().slice(0, 10);
}

function initialState(): PomoState {
  return { phase: 'focus', running: false, endsAt: 0, pausedMs: DEFAULT_SETTINGS.focusMin * 60000, cycles: 0 };
}

function load(): Persisted {
  const base: Persisted = {
    settings: DEFAULT_SETTINGS,
    pos: DEFAULT_POS,
    state: initialState(),
    sessionsToday: 0,
    sessionsDate: todayKey()
  };
  try {
    const raw = localStorage.getItem(LS);
    if (!raw) return base;
    const p = JSON.parse(raw) as Partial<Persisted>;
    const settings = { ...DEFAULT_SETTINGS, ...(p.settings ?? {}) };
    let state = { ...initialState(), ...(p.state ?? {}) };
    // reset an interrupted run so the widget opens paused, not mid-count
    if (state.running && state.endsAt <= Date.now()) {
      const phaseLen = phaseLenMs(state.phase, settings);
      state = { ...state, running: false, endsAt: 0, pausedMs: Math.max(0, state.endsAt - Date.now()) || 0 };
      if (state.pausedMs <= 0) state.pausedMs = phaseLen;
    }
    const sameDay = p.sessionsDate === todayKey();
    return {
      settings,
      pos: { ...DEFAULT_POS, ...(p.pos ?? {}) },
      state,
      sessionsToday: sameDay ? p.sessionsToday ?? 0 : 0,
      sessionsDate: todayKey()
    };
  } catch {
    return base;
  }
}

function phaseLenMs(phase: Phase, s: PomoSettings): number {
  return (phase === 'focus' ? s.focusMin : phase === 'long' ? s.longMin : s.shortMin) * 60000;
}

type Ctx = {
  settings: PomoSettings;
  state: PomoState;
  pos: PomoPos;
  panelOpen: boolean;
  minimized: boolean;
  sessionsToday: number;
  remainingMs: number;
  totalMs: number;
  setSettings: (patch: Partial<PomoSettings>) => void;
  setPos: (pos: PomoPos) => void;
  start: () => void;
  pause: () => void;
  toggle: () => void;
  reset: () => void;
  skip: () => void;
  setPhase: (phase: Phase) => void;
  openTool: () => void;
  closeTool: () => void;
  minimize: (v: boolean) => void;
};

const PomodoroContext = createContext<Ctx | null>(null);

export function PomodoroProvider({ children }: { children: ReactNode }) {
  const initial = useRef<Persisted>(load());
  const [settings, setSettingsState] = useState<PomoSettings>(initial.current.settings);
  const [state, setState] = useState<PomoState>(initial.current.state);
  const [pos, setPosState] = useState<PomoPos>(initial.current.pos);
  const [panelOpen, setPanelOpen] = useState(false);
  const [minimized, setMinimized] = useState(false);
  const [sessionsToday, setSessionsToday] = useState(initial.current.sessionsToday);
  const [, setTick] = useState(0);

  const settingsRef = useRef(settings);
  settingsRef.current = settings;

  // keep sessionsToday fresh if the day rolls over while open
  useEffect(() => {
    const t = window.setInterval(() => {
      setSessionsToday(cur => (load().sessionsDate === todayKey() ? cur : 0));
    }, 60000);
    return () => window.clearInterval(t);
  }, []);

  // persist whenever anything durable changes
  useEffect(() => {
    try {
      localStorage.setItem(LS, JSON.stringify({ settings, state, pos, sessionsToday, sessionsDate: todayKey() }));
    } catch {
      /* storage unavailable */
    }
  }, [settings, state, pos, sessionsToday]);

  // ticking while running (drives countdown + the auto-advance edge check)
  useEffect(() => {
    if (!state.running) return;
    const id = window.setInterval(() => setTick(t => t + 1), 250);
    return () => window.clearInterval(id);
  }, [state.running]);

  const beep = useCallback(() => {
    try {
      const AC = window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
      if (!AC) return;
      const ac = new AC();
      const notes = [880, 660, 990];
      notes.forEach((f, i) => {
        const o = ac.createOscillator();
        const g = ac.createGain();
        o.frequency.value = f;
        o.type = 'sine';
        g.gain.value = 0.0001;
        const start = ac.currentTime + i * 0.18;
        g.gain.setValueAtTime(0.0001, start);
        g.gain.exponentialRampToValueAtTime(0.15, start + 0.02);
        g.gain.exponentialRampToValueAtTime(0.0001, start + 0.16);
        o.connect(g);
        g.connect(ac.destination);
        o.start(start);
        o.stop(start + 0.18);
      });
      window.setTimeout(() => ac.close(), 1400);
    } catch {
      /* audio blocked — ignore */
    }
  }, []);

  const advance = useCallback(
    (fromPhase: Phase) => {
      const s = settingsRef.current;
      beep();
      if (fromPhase === 'focus') {
        const cycles = stateRef.current.cycles + 1;
        markActiveToday(); // focus session counts toward the daily streak
        setSessionsToday(c => c + 1);
        const nextPhase: Phase = cycles % s.longEvery === 0 ? 'long' : 'short';
        toast(`Focus done — ${nextPhase === 'long' ? 'long' : 'short'} break. 🍅`, 'ok');
        setState({ phase: nextPhase, running: s.autoStartBreaks, endsAt: s.autoStartBreaks ? Date.now() + phaseLenMs(nextPhase, s) : 0, pausedMs: phaseLenMs(nextPhase, s), cycles });
      } else {
        toast('Break over — back to focus.', 'ok');
        setState({ phase: 'focus', running: false, endsAt: 0, pausedMs: phaseLenMs('focus', s), cycles: stateRef.current.cycles });
      }
    },
    [beep]
  );

  const stateRef = useRef(state);
  stateRef.current = state;

  // detect an elapsed phase while running
  useEffect(() => {
    if (!state.running) return;
    if (Date.now() >= state.endsAt) {
      advance(state.phase);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.running, state.endsAt, advance]);

  const setSettings = useCallback((patch: Partial<PomoSettings>) => {
    const next = { ...settingsRef.current, ...patch };
    settingsRef.current = next;
    const s = stateRef.current;
    if (s.running) {
      // rescale remaining time to the new length of the current phase
      const left = Math.max(0, s.endsAt - Date.now());
      const oldLen = phaseLenMs(s.phase, settings);
      const newLen = phaseLenMs(s.phase, next);
      const scaled = oldLen > 0 ? Math.round((left / oldLen) * newLen) : newLen;
      setState({ ...s, endsAt: Date.now() + scaled, pausedMs: scaled });
    } else {
      setState({ ...s, pausedMs: phaseLenMs(s.phase, next) });
    }
    setSettingsState(next);
  }, [settings]);

  const setPhase = useCallback((phase: Phase) => {
    const s = settingsRef.current;
    setState(st => ({ ...st, phase, running: false, endsAt: 0, pausedMs: phaseLenMs(phase, s) }));
  }, []);

  const start = useCallback(() => {
    setState(s => {
      const ms = s.pausedMs > 0 ? s.pausedMs : phaseLenMs(s.phase, settingsRef.current);
      return { ...s, running: true, endsAt: Date.now() + ms, pausedMs: ms };
    });
  }, []);

  const pause = useCallback(() => {
    setState(s => (s.running ? { ...s, running: false, pausedMs: Math.max(0, s.endsAt - Date.now()) } : s));
  }, []);

  const toggle = useCallback(() => (stateRef.current.running ? pause() : start()), [pause, start]);

  const reset = useCallback(() => {
    const s = settingsRef.current;
    setState(st => ({ ...st, running: false, endsAt: 0, pausedMs: phaseLenMs(st.phase, s) }));
  }, []);

  const skip = useCallback(() => advance(stateRef.current.phase), [advance]);

  const openTool = useCallback(() => {
    setPanelOpen(true);
    setMinimized(false);
  }, []);
  const closeTool = useCallback(() => setPanelOpen(false), []);
  const minimize = useCallback((v: boolean) => setMinimized(v), []);
  const setPos = useCallback((p: PomoPos) => setPosState(p), []);

  // Recomputed on every render; the tick interval re-renders while running.
  const remainingMs = state.running ? Math.max(0, state.endsAt - Date.now()) : Math.max(0, state.pausedMs);
  const totalMs = phaseLenMs(state.phase, settings);

  const value: Ctx = {
    settings,
    state,
    pos,
    panelOpen,
    minimized,
    sessionsToday,
    remainingMs,
    totalMs,
    setSettings,
    setPos,
    start,
    pause,
    toggle,
    reset,
    skip,
    setPhase,
    openTool,
    closeTool,
    minimize
  };

  return <PomodoroContext.Provider value={value}>{children}</PomodoroContext.Provider>;
}

export function usePomodoro(): Ctx {
  const c = useContext(PomodoroContext);
  if (!c) throw new Error('usePomodoro must be used inside <PomodoroProvider>');
  return c;
}

export function fmt(ms: number): string {
  const s = Math.max(0, Math.ceil(ms / 1000));
  const m = Math.floor(s / 60);
  return `${m}:${String(s % 60).padStart(2, '0')}`;
}
